// import { useState } from "react";

import { Button, Modal, notification, Tag } from "antd";
import { ErrorAlert, OTPCountdown } from "components/Common";
import InputField from "components/Common/InputField";
import { Formik } from "formik";
import { Edit, Lock } from "lucide-react";
import { useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  useChangePasswordMutation,
  useCheckEmailMutation,
  useCheckOldPasswordMutation,
  useSendOtpMutation,
  useSendOtpNewEmailMutation,
  useVerifyOtpMutation,
} from "store/api";
import {
  selectUser,
  setIsFirstTimeLogin,
  updateUserDetails,
} from "store/slices";
import * as Yup from "yup";

const Profile = () => {
  const { modalOpen } = useLocation().state || {};
  const [isEditModalVisible, setIsEditModalVisible] = useState(
    modalOpen || false,
  );
  const [error, setError] = useState<string | null>(null);
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const [sendOtp, { isLoading: isSendingOtp }] = useSendOtpMutation();
  const [sendOtpNewEmail, { isLoading: isSendingOtpNewEmail }] =
    useSendOtpNewEmailMutation();
  const [verifyOtp, { isLoading: isVerifyingOtp }] = useVerifyOtpMutation();
  const [step, setStep] = useState(0);
  const [emailStep, setEmailStep] = useState(0);
  const [editEmail, setEditEmail] = useState(false);
  const [checkOldPassword, { isLoading: isCheckingPassword }] =
    useCheckOldPasswordMutation();
  const [checkEmail, { isLoading: isCheckingEmail }] = useCheckEmailMutation();
  const [startCounting, setStartCounting] = useState(false);

  const sendOtpHandler = async () => {
    try {
      await sendOtp({ email: user?.email }).unwrap();
      setStep(2);
      setEmailStep(1);
      setStartCounting(true);
      notification.success({
        message: "OTP sent",
        description:
          "OTP has been sent to your, Please check your registered email inbox",
      });
    } catch (error: any) {
      setError(
        error?.data?.error || "Failed to send OTP. Please try again later.",
      );
    }
  };

  return (
    <div className="w-full max-w-full mx-auto">
      <div className="mt-6 max-w-full mx-auto">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          {/* Profile Image Section */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="relative w-fit">
              {user?.profileImage ? (
                <img
                  src={user?.profile_image}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="w-24 h-24 flex items-center justify-center bg-blue-400 text-white rounded-full object-cover border-2 border-gray-200 uppercase text-3xl font-bold">
                  {user?.username?.charAt(0) ||
                    `${user?.firstName?.charAt(0)} ${user?.lastName?.charAt(0)}`}
                </div>
              )}

              {/* <button
                onClick={() => {}}
                className="absolute bottom-0 right-0 bg-white rounded-full p-1 border border-gray-300 hover:bg-gray-100 transition"
                title="Edit Photo"
              >
                <Edit size={16} className="#31383e" />
              </button> */}
            </div>
          </div>

          {/* Profile Fields */}
          <div className="space-y-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              {
                label: "Full Name",
                value: `${user?.firstName || ""} ${user?.lastName || ""}`,
              },
              {
                label: "Role",
                value: `${user?.role || ""}`,
              },
              {
                label: "Group Name",
                value: `${user?.serviceProvider || ""}`,
              },
              {
                label: "Account Number",
                value: user?.accountNumber,
              },
              {
                label: "Email",
                value: (
                  <a className=" !lowercase" href={`mailto:${user?.email}`}>
                    {user?.email}
                  </a>
                ),
                onEdit: () => {
                  setEditEmail(true);
                },
              },
              {
                label: "Phone Number",
                value: <a href={`tel:${user?.phone}`}>{user?.phone}</a>,
              },
              {
                label: "Account Status",
                value: (
                  <Tag color={user?.isVerified === "true" ? "green" : "yellow"}>
                    {user?.isVerified === "true" ? "Verified" : "Not verified"}
                  </Tag>
                ),
              },
            ].map(
              ({
                label,
                value,
                onEdit,
              }: {
                label: any;
                value: any;
                onEdit?: () => void;
              }) => (
                <div key={label} className="border-b border-gray-200 pb-4">
                  <div className="flex gap-3 items-center mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {label}
                    </span>
                    {onEdit && (
                      <button
                        // onClick={() => handleEdit(label, value)}
                        onClick={onEdit}
                        className="text-[#31383e] flex items-center gap-1 text-sm"
                      >
                        <Edit size={14} /> Edit
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 capitalize font-medium">
                    {value}
                  </p>
                </div>
              ),
            )}

            {/* Password */}
            <div className="pb-2">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-900">
                  Password
                </span>
                <button
                  onClick={() => setIsEditModalVisible(true)}
                  className="text-[#31383e] flex items-center gap-1 text-sm"
                >
                  <Lock size={14} /> Change Password
                </button>
              </div>
              <p className="text-sm text-gray-500">•••••••••••</p>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={isEditModalVisible}
        footer={null}
        onCancel={() => {
          setIsEditModalVisible(false);
          window.location.reload();
        }}
        title="Update Password"
        centered
        closable={modalOpen ? false : true}
        maskClosable={modalOpen ? false : true}
      >
        <Formik
          validateOnBlur
          initialValues={{
            newPassword: "",
            confirmPassword: "",
            oldPassword: "",
            otp: "",
          }}
          validationSchema={Yup.object().shape({
            newPassword: Yup.string()
              .required("Password is required")
              .min(14, "Password must be at least 14 characters")
              .matches(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                "Password must contain at least one uppercase letter (A-Z), one lowercase letter (a-z), one number (0-9), and one special character (@$!%*?&)",
              )
              .not(
                [Yup.ref("oldPassword"), ""],
                "New password must be different from old password",
              ),
            confirmPassword: Yup.string()
              .required("Confirm password is required")
              .oneOf([Yup.ref("newPassword"), ""], "Passwords don't match"),
            oldPassword: Yup.string().required("Old password is required"),
          })}
          onSubmit={async (values, { resetForm }) => {
            try {
              await verifyOtp({
                email: user?.email,
                otp: +values.otp,
                endpoint: "verify-otp",
              }).unwrap();
              await changePassword(values).unwrap();
              setIsEditModalVisible(false);
              notification.success({
                message: "Password changed successfully",
              });
              dispatch(setIsFirstTimeLogin(false));
              resetForm();
            } catch (error: any) {
              setError(
                error?.data?.error ||
                  "Failed to change password. Please try again.",
              );
            }
          }}
        >
          {({ handleSubmit, isValid, dirty, values }) => {
            const oldPasswordCheck = async () => {
              try {
                await checkOldPassword({
                  oldPassword: values.oldPassword,
                }).unwrap();
                setStep(1);
              } catch (error: any) {
                setError(
                  error?.data?.error ||
                    error?.data?.message ||
                    "Please try again.",
                );
              }
            };
            return (
              <form onSubmit={handleSubmit} className="space-y-3">
                <ErrorAlert
                  type="error"
                  message={error}
                  setMessage={setError}
                />
                {step === 0 && (
                  <>
                    <InputField
                      label="Old Password"
                      name="oldPassword"
                      type="password"
                      placeholder="Enter Old password"
                    />
                    <Button
                      type="primary"
                      htmlType="button"
                      className="w-full h-10"
                      onClick={oldPasswordCheck}
                      loading={isCheckingPassword}
                      disabled={!values.oldPassword || isCheckingPassword}
                    >
                      Continue
                    </Button>
                  </>
                )}
                {step === 1 && (
                  <>
                    <InputField
                      label="New Password"
                      name="newPassword"
                      type="password"
                      placeholder="Enter New password"
                    />
                    <InputField
                      label="Confirm Password"
                      name="confirmPassword"
                      type="password"
                      placeholder="Confirm password"
                    />

                    <Button
                      onClick={sendOtpHandler}
                      type="primary"
                      htmlType="button"
                      className="w-full h-10"
                      loading={
                        !values.newPassword ||
                        !values.oldPassword ||
                        isLoading ||
                        isSendingOtp
                      }
                      disabled={!isValid || !dirty || isLoading || isSendingOtp}
                    >
                      Update Password
                    </Button>
                  </>
                )}
                {step === 2 && (
                  <>
                    <div>
                      {" "}
                      <InputField
                        label="OTP"
                        name="otp"
                        type="text"
                        placeholder="Enter OTP"
                      />
                      <OTPCountdown
                        isSendingOtp={isSendingOtp}
                        expiryMinutes={10}
                        onResend={sendOtpHandler}
                        start={startCounting}
                        setStart={setStartCounting}
                      />
                    </div>

                    <Button
                      type="primary"
                      htmlType="submit"
                      className="w-full h-10"
                      loading={isLoading || isVerifyingOtp}
                      disabled={
                        !values.otp ||
                        isLoading ||
                        isSendingOtp ||
                        isVerifyingOtp
                      }
                    >
                      Verify OTP
                    </Button>
                  </>
                )}
              </form>
            );
          }}
        </Formik>
      </Modal>
      <Modal
        open={editEmail}
        onCancel={() => {
          setEditEmail(false);
          window.location.reload();
        }}
        onClose={() => {
          setEditEmail(false);
          window.location.reload();
        }}
        footer={null}
        centered
        title="Update Email"
      >
        <Formik
          initialValues={{
            email: "",
            otp: "",
          }}
          validationSchema={Yup.object().shape({
            email: Yup.string()
              .email("Enter valid email")
              .required("Email is required"),
            otp: Yup.string().required("OTP is required"),
          })}
          onSubmit={async (values, { resetForm }) => {
            try {
              const res = await verifyOtp({
                email: values.email,
                otp: +values.otp,
                endpoint: "verify-change-email-otp",
              }).unwrap();
              dispatch(updateUserDetails({ user: res?.user }));

              setEditEmail(false);
              setEmailStep(0);
              notification.success({
                message: "Password changed successfully",
              });
              resetForm();
            } catch (error: any) {
              setError(
                error?.data?.error ||
                  "Failed to change password. Please try again.",
              );
            }
          }}
        >
          {({ values, handleSubmit }) => {
            const sendOtpHandler = async () => {
              try {
                await checkEmail({ email: values.email }).unwrap();
                await sendOtpNewEmail({ newEmail: values.email }).unwrap();

                setEmailStep(1);
                notification.success({
                  message: "OTP sent",
                  description:
                    "OTP has been sent to your email, Please check your email inbox",
                });
              } catch (error: any) {
                setError(
                  error?.data?.error ||
                    error?.data?.message ||
                    "Failed to send OTP. Please try again later.",
                );
              }
            };
            return (
              <form className="space-y-3" onSubmit={handleSubmit}>
                <ErrorAlert
                  type="error"
                  message={error}
                  setMessage={setError}
                />{" "}
                {emailStep === 0 && (
                  <>
                    {" "}
                    <InputField
                      label="Email"
                      name="email"
                      type="email"
                      placeholder="Enter new email"
                    />
                    <Button
                      type="primary"
                      htmlType="button"
                      className="w-full h-10"
                      onClick={sendOtpHandler}
                      loading={isSendingOtp}
                      disabled={
                        !values.email || isSendingOtpNewEmail || isCheckingEmail
                      }
                    >
                      Continue
                    </Button>
                  </>
                )}{" "}
                {emailStep === 1 && (
                  <>
                    <InputField
                      label="OTP"
                      name="otp"
                      type="text"
                      placeholder="Enter OTP"
                    />
                    <OTPCountdown
                      isSendingOtp={isSendingOtpNewEmail}
                      expiryMinutes={10}
                      onResend={sendOtpHandler}
                      start={startCounting}
                      setStart={setStartCounting}
                    />
                    <Button
                      type="primary"
                      htmlType="submit"
                      className="w-full h-10"
                      loading={isVerifyingOtp}
                      disabled={!values.otp || isVerifyingOtp}
                    >
                      Verify OTP
                    </Button>
                  </>
                )}
              </form>
            );
          }}
        </Formik>
      </Modal>
    </div>
  );
};

export default Profile;
